import React, { useCallback } from 'react';

import { AccessibilityPropsAndroid, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    DETAIL_AGE_RATING_ICON,
    DETAIL_CATCHUP_ICON,
    DETAIL_LOCK_ICON,
    DETAIL_RECORDING_ICON,
    DETAIL_REPLAY_ICON,
    DETAIL_SUBSCRIBE_ICON,
    UNSUBSCRIBED_MARKER_ID,
} from 'config/testID';

import { CachedRecordingItem } from 'store/recordings/reducer.types';

import { EventParentalControlRating } from 'services/data-manager/types';

import { isWeb } from 'utils/responsiveness';

import CatchupIcon from 'components/CatchupIcon';
import ParentalRatingMarker from 'components/ContentMarkerIcons/ParentalRatingMarker';
import SubscribeIcon from 'components/ContentMarkerIcons/SubscribeMarker/SubscribeIcon';
import styles from 'components/ContentMarkerIcons/styles';
import getMostSpecificIconType, {
    ContentMarkerType,
} from 'components/ContentMarkerIcons/utils/getMostSpecificIconType';
import LockIcon from 'components/LockIcon/LockIcon';

import RecordingIcon from './RecordingMarker';
import ReplayIcon from './ReplayIcon';

type Props = {
    shouldDisplayCatchup?: boolean;
    shouldDisplayStartOver?: boolean;
    shouldDisplaySubscribe?: boolean;
    isLimitedToOne?: boolean;
    parentalRating?: EventParentalControlRating | undefined;
    isOnLiveCard?: boolean;
    isOnCard?: boolean;
    style?: {};
    iconStyle?: {};
    parentalRatingTestID?: string;
    recordingItem?: CachedRecordingItem;
    isContentLocked?: boolean;
    collectionSuspensionActive?: boolean;
    importantForAccessibility?: AccessibilityPropsAndroid['importantForAccessibility'];
    tabIndex?: 0 | -1;
};

const {
    ACCESSIBILITY: {
        DETAIL_PAGE: { ACCESSIBILITY_ICON_LOCKED },
    },
} = LANGUAGE_KEYS;

// If it is limited to one, will return the most specific content marker icon
const ContentMarkerIcons = ({
    isLimitedToOne = false,
    parentalRating,
    isOnLiveCard = false,
    isOnCard = false,
    style = {},
    iconStyle = {},
    shouldDisplayCatchup = false,
    shouldDisplayStartOver = false,
    shouldDisplaySubscribe = false,
    parentalRatingTestID,
    recordingItem,
    isContentLocked,
    collectionSuspensionActive,
    importantForAccessibility,
    tabIndex = 0,
}: Props) => {
    const { t } = useTranslation();

    let type: ContentMarkerType | null;

    if (isOnCard) {
        type = ContentMarkerType.PARENTAL_RATING;
    } else {
        type = isLimitedToOne
            ? getMostSpecificIconType({
                  parentalRating,
                  shouldDisplayCatchup,
                  shouldDisplayStartOver,
              })
            : null;
    }

    const renderSubscribeMarker = useCallback(() => {
        if (shouldDisplaySubscribe) {
            return (
                <View
                    style={[
                        isLimitedToOne && styles.cardContentMarkerIcons,
                        isLimitedToOne && isOnLiveCard && styles.liveCard,
                    ]}
                    testID={DETAIL_SUBSCRIBE_ICON}
                >
                    <SubscribeIcon
                        subscribeTestID={UNSUBSCRIBED_MARKER_ID}
                        style={isLimitedToOne && styles.noMarginRight}
                        {...(isWeb && { tabIndex })}
                    />
                </View>
            );
        }

        return null;
    }, [shouldDisplaySubscribe, isOnLiveCard, isLimitedToOne, tabIndex]);

    const renderParentalRatingMarker = useCallback(() => {
        return (
            <View
                style={[
                    isLimitedToOne && styles.cardContentMarkerIcons,
                    isLimitedToOne && isOnLiveCard && styles.liveCard,
                ]}
                testID={DETAIL_AGE_RATING_ICON}
            >
                <ParentalRatingMarker
                    parentalRatingTestID={parentalRatingTestID}
                    parentalRating={parentalRating}
                    style={isLimitedToOne && styles.noMarginRight}
                    {...(isWeb && { tabIndex })}
                />
            </View>
        );
    }, [
        parentalRating,
        isOnLiveCard,
        isLimitedToOne,
        parentalRatingTestID,
        tabIndex,
    ]);

    const renderStartOverMarker = useCallback(() => {
        if (shouldDisplayStartOver) {
            return (
                <View
                    style={[
                        isLimitedToOne && styles.cardContentMarkerIcons,
                        isLimitedToOne && isOnLiveCard && styles.liveCard,
                    ]}
                    testID={DETAIL_REPLAY_ICON}
                >
                    <ReplayIcon
                        ableToStartOver={shouldDisplayStartOver}
                        style={iconStyle}
                        {...(isWeb && { tabIndex })}
                    />
                </View>
            );
        }

        return null;
    }, [
        iconStyle,
        isOnLiveCard,
        isLimitedToOne,
        shouldDisplayStartOver,
        tabIndex,
    ]);

    const renderCatchupMarker = useCallback(() => {
        if (shouldDisplayCatchup && !recordingItem) {
            return (
                <View
                    style={[
                        isLimitedToOne && styles.cardContentMarkerIcons,
                        isLimitedToOne && isOnLiveCard && styles.liveCard,
                    ]}
                    testID={DETAIL_CATCHUP_ICON}
                >
                    <CatchupIcon
                        ableToPlayCatchup={shouldDisplayCatchup}
                        style={iconStyle}
                        {...(isWeb && { tabIndex })}
                    />
                </View>
            );
        }

        return null;
    }, [
        shouldDisplayCatchup,
        recordingItem,
        isLimitedToOne,
        isOnLiveCard,
        iconStyle,
        tabIndex,
    ]);

    const renderRecordingMarker = useCallback(() => {
        if (recordingItem) {
            return (
                <View
                    style={[
                        isLimitedToOne && styles.cardContentMarkerIcons,
                        isLimitedToOne && isOnLiveCard && styles.liveCard,
                    ]}
                    testID={DETAIL_RECORDING_ICON}
                >
                    <RecordingIcon
                        recordingItem={recordingItem}
                        style={iconStyle}
                        collectionSuspensionActive={collectionSuspensionActive}
                        {...(isWeb && { tabIndex })}
                    />
                </View>
            );
        }

        return null;
    }, [
        recordingItem,
        isLimitedToOne,
        isOnLiveCard,
        iconStyle,
        collectionSuspensionActive,
        tabIndex,
    ]);

    const renderLockIcon = useCallback(() => {
        if (isContentLocked) {
            return (
                <View
                    style={[
                        isLimitedToOne && styles.cardContentMarkerIcons,
                        isLimitedToOne && isOnLiveCard && styles.liveCard,
                    ]}
                    accessibilityLabel={t(ACCESSIBILITY_ICON_LOCKED)}
                    accessibilityRole="image"
                    accessible
                    {...(isWeb && { tabIndex })}
                    testID={DETAIL_LOCK_ICON}
                >
                    <LockIcon />
                </View>
            );
        }

        return null;
    }, [isContentLocked, isOnLiveCard, isLimitedToOne, tabIndex]);

    const renderAllMarkers = useCallback(() => {
        return (
            <View style={[styles.rowContentMarkerIcons, style]}>
                {renderLockIcon()}
                {renderSubscribeMarker()}
                {renderParentalRatingMarker()}
                {renderRecordingMarker() || renderStartOverMarker()}
                {renderLockIcon()}
                {renderCatchupMarker()}
            </View>
        );
    }, [
        renderParentalRatingMarker,
        renderCatchupMarker,
        style,
        renderLockIcon,
        renderSubscribeMarker,
        renderStartOverMarker,
        renderRecordingMarker,
    ]);

    return (
        <View importantForAccessibility={importantForAccessibility}>
            {type === ContentMarkerType.PARENTAL_RATING &&
                renderParentalRatingMarker()}
            {type === ContentMarkerType.CATCHUP && renderParentalRatingMarker()}
            {type === ContentMarkerType.STARTOVER && renderStartOverMarker()}
            {!type && renderAllMarkers()}
        </View>
    );
};

export default ContentMarkerIcons;
